import React, { useEffect, useRef, useState } from "react";

type Board = {
  id: string;
  name: string;
  key: string;
  owner: string;
  shared: boolean;
  createdAt: string;
  updatedAt: string;
};

type Checkpoint = { ts: number; author?: string | null };

const formatAge = (isoOrTs: string | number) => {
  const d = typeof isoOrTs === "number" ? new Date(isoOrTs) : new Date(isoOrTs);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const BoardCard: React.FC<{
  board: Board;
  isOwner: boolean;
  isCurrent: boolean;
  onOpen: () => void;
  onToggleShared: () => void;
  onDelete: () => void;
  onViewHistory: () => void;
  checkpoints?: Checkpoint[];
  loadingCheckpoints: boolean;
  onRestore: (ts: number) => void;
}> = ({
  board, isOwner, isCurrent, onOpen,
  onToggleShared, onDelete, onViewHistory,
  checkpoints, loadingCheckpoints, onRestore,
}) => {
  const [hovered, setHovered] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [thumbError, setThumbError] = useState(false);

  const toggleHistory = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!showHistory) onViewHistory();
    setShowHistory(p => !p);
  };

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setConfirmDelete(false); }}
    >
      {/* Thumbnail */}
      <div
        onClick={onOpen}
        style={{
          position: "relative",
          paddingTop: "62.5%",
          background: "#11111b",
          borderRadius: 8,
          overflow: "hidden",
          border: `2px solid ${isCurrent ? "#89b4fa" : "#313244"}`,
          cursor: "pointer",
          transition: "border-color 0.1s",
        }}
      >
        {!thumbError ? (
          <img
            src={`/api/v2/scenes/${board.id}/thumb?v=${new Date(board.updatedAt).getTime()}`}
            onError={() => setThumbError(true)}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#313244", fontSize: 36,
          }}>
            {"✏"}
          </div>
        )}

        {/* Hover overlay */}
        {hovered && (
          <div
            style={{
              position: "absolute", inset: 0,
              background: "rgba(17,17,27,0.88)",
              display: "flex", alignItems: "center", justifyContent: "center",
              gap: 8,
            }}
          >
            {isOwner && (
              <button
                onClick={e => { e.stopPropagation(); onToggleShared(); }}
                title={board.shared ? "Remove from team" : "Share with team"}
                style={overlayBtn(board.shared ? "#a6e3a1" : undefined)}
              >
                {board.shared ? "Team ✓" : "Share"}
              </button>
            )}
            <button onClick={toggleHistory} title="History" style={overlayBtn(showHistory ? "#313244" : undefined)}>
              {"⏱"}
            </button>
            {isOwner && (
              confirmDelete ? (
                <>
                  <button onClick={e => { e.stopPropagation(); onDelete(); }} style={overlayBtn("#f38ba8")}>
                    Delete?
                  </button>
                  <button onClick={e => { e.stopPropagation(); setConfirmDelete(false); }} style={overlayBtn()}>
                    {"×"}
                  </button>
                </>
              ) : (
                <button onClick={e => { e.stopPropagation(); setConfirmDelete(true); }} title="Delete" style={overlayBtn()}>
                  {"🗑"}
                </button>
              )
            )}
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: "6px 2px 0" }}>
        <div style={{
          fontWeight: 500, fontSize: 13,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          color: isCurrent ? "#89b4fa" : "#cdd6f4",
        }}>
          {board.name}
        </div>
        <div style={{ fontSize: 11, color: "#6c7086", display: "flex", gap: 6, alignItems: "center", marginTop: 2 }}>
          {!isOwner && <span style={{ color: "#a6adc8" }}>@{board.owner}</span>}
          <span>{formatAge(board.updatedAt)}</span>
          {board.shared && (
            <span style={{
              background: "#a6e3a1", color: "#1e1e2e",
              borderRadius: 4, padding: "1px 5px", fontSize: 10, fontWeight: 600,
            }}>Team</span>
          )}
        </div>
      </div>

      {/* History panel */}
      {showHistory && (
        <div style={{
          marginTop: 6, background: "#181825",
          borderRadius: 6, border: "1px solid #313244",
          maxHeight: 130, overflowY: "auto",
        }}>
          {loadingCheckpoints ? (
            <p style={{ color: "#6c7086", padding: "8px 10px", margin: 0, fontSize: 12 }}>Loading...</p>
          ) : !checkpoints?.length ? (
            <p style={{ color: "#6c7086", padding: "8px 10px", margin: 0, fontSize: 12 }}>No history yet</p>
          ) : checkpoints.map(cp => (
            <div key={cp.ts} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "5px 10px", borderBottom: "1px solid #313244",
            }}>
              <span style={{ color: "#a6adc8", fontSize: 12 }}>
                {cp.author && <span style={{ color: "#89b4fa", marginRight: 5 }}>{cp.author}</span>}
                {formatAge(cp.ts)}
              </span>
              <button
                onClick={() => onRestore(cp.ts)}
                style={{
                  background: "none", border: "1px solid #45475a",
                  borderRadius: 4, color: "#cdd6f4", padding: "2px 8px",
                  cursor: "pointer", fontSize: 11,
                }}
              >Restore</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

function overlayBtn(bg?: string): React.CSSProperties {
  const dark = bg === "#f38ba8" || bg === "#a6e3a1";
  return {
    background: bg ?? "rgba(49,50,68,0.95)",
    border: "none",
    borderRadius: 6,
    color: dark ? "#1e1e2e" : "#cdd6f4",
    padding: "6px 11px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 500,
  };
}

export const BoardPicker: React.FC<{
  onClose: () => void;
  onSelect: (id: string, key: string, name: string) => void;
  onRestore: (id: string, key: string, ts: number) => void;
  onToggleShared: (id: string, shared: boolean) => Promise<void>;
  onImportFile: (file: File) => void;
  currentBoardId?: string | null;
  currentUserLogin: string | null;
  onDeletedCurrentBoard?: () => void;
}> = ({
  onClose, onSelect, onRestore, onToggleShared, onImportFile,
  currentBoardId, currentUserLogin, onDeletedCurrentBoard,
}) => {
  const [boards, setBoards] = useState<Board[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkpoints, setCheckpoints] = useState<Record<string, Checkpoint[]>>({});
  const [loadingCps, setLoadingCps] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const token = localStorage.getItem("syntrofi_jwt");
    fetch("/api/v2/boards", {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.json())
      .then(data => { setBoards(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleDelete = async (id: string) => {
    const token = localStorage.getItem("syntrofi_jwt");
    await fetch(`/api/v2/scenes/${id}`, {
      method: "DELETE",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    setBoards(prev => prev.filter(b => b.id !== id));
    if (id === currentBoardId) onDeletedCurrentBoard?.();
  };

  const handleToggleShared = async (board: Board) => {
    const next = !board.shared;
    await onToggleShared(board.id, next);
    setBoards(prev => prev.map(b => b.id === board.id ? { ...b, shared: next } : b));
  };

  const handleViewHistory = async (board: Board) => {
    if (checkpoints[board.id]) return;
    setLoadingCps(board.id);
    try {
      const cps = await fetch(`/api/v2/scenes/${board.id}/checkpoints`).then(r => r.json());
      setCheckpoints(prev => ({ ...prev, [board.id]: cps }));
    } finally {
      setLoadingCps(null);
    }
  };

  const myBoards = boards.filter(b => b.owner === currentUserLogin);
  const teamBoards = boards.filter(b => b.owner !== currentUserLogin && b.shared);

  const sectionLabel: React.CSSProperties = {
    fontSize: 11, fontWeight: 600, color: "#6c7086",
    textTransform: "uppercase", letterSpacing: "0.08em",
    margin: "0 0 14px",
  };

  const grid: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: 18,
    marginBottom: 28,
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.72)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 10000,
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: "#1e1e2e",
        borderRadius: 14,
        padding: "28px 28px 24px",
        width: 700,
        maxHeight: "78vh",
        display: "flex",
        flexDirection: "column",
        color: "#cdd6f4",
        boxShadow: "0 12px 48px rgba(0,0,0,0.65)",
      }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Boards</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#6c7086", fontSize: 22, cursor: "pointer", lineHeight: 1, padding: "0 2px" }}>
            {"×"}
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{ overflowY: "auto", flex: 1 }}>
          {loading ? (
            <p style={{ color: "#6c7086", textAlign: "center", marginTop: 48 }}>Loading...</p>
          ) : (
            <>
              {/* My Boards */}
              <p style={sectionLabel}>My Boards</p>
              <div style={grid}>
                {myBoards.map(board => (
                  <BoardCard
                    key={board.id}
                    board={board}
                    isOwner
                    isCurrent={board.id === currentBoardId}
                    onOpen={() => { onSelect(board.id, board.key, board.name); onClose(); }}
                    onToggleShared={() => handleToggleShared(board)}
                    onDelete={() => handleDelete(board.id)}
                    onViewHistory={() => handleViewHistory(board)}
                    checkpoints={checkpoints[board.id]}
                    loadingCheckpoints={loadingCps === board.id}
                    onRestore={ts => onRestore(board.id, board.key, ts)}
                  />
                ))}

                {/* Import from file */}
                <div>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      position: "relative",
                      paddingTop: "62.5%",
                      background: "none",
                      border: "2px dashed #313244",
                      borderRadius: 8,
                      cursor: "pointer",
                    }}
                  >
                    <div style={{
                      position: "absolute", inset: 0,
                      display: "flex", flexDirection: "column",
                      alignItems: "center", justifyContent: "center",
                      color: "#45475a",
                    }}>
                      <div style={{ fontSize: 30, lineHeight: 1, fontWeight: 300 }}>+</div>
                    </div>
                  </div>
                  <div style={{ padding: "6px 2px 0" }}>
                    <div style={{ fontSize: 13, color: "#6c7086" }}>Import file</div>
                    <div style={{ fontSize: 11, color: "#45475a", marginTop: 2 }}>.excalidraw</div>
                  </div>
                </div>
              </div>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".excalidraw,.excalidraw.png,.png,.json"
                style={{ display: "none" }}
                onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) { onImportFile(file); onClose(); }
                  e.target.value = "";
                }}
              />

              {/* Team Boards */}
              {teamBoards.length > 0 && (
                <>
                  <p style={{ ...sectionLabel, marginTop: 4 }}>Team</p>
                  <div style={grid}>
                    {teamBoards.map(board => (
                      <BoardCard
                        key={board.id}
                        board={board}
                        isOwner={false}
                        isCurrent={board.id === currentBoardId}
                        onOpen={() => { onSelect(board.id, board.key, board.name); onClose(); }}
                        onToggleShared={() => {}}
                        onDelete={() => {}}
                        onViewHistory={() => handleViewHistory(board)}
                        checkpoints={checkpoints[board.id]}
                        loadingCheckpoints={loadingCps === board.id}
                        onRestore={ts => onRestore(board.id, board.key, ts)}
                      />
                    ))}
                  </div>
                </>
              )}

              {myBoards.length === 0 && teamBoards.length === 0 && (
                <p style={{ color: "#6c7086", textAlign: "center", marginTop: 40 }}>
                  No boards yet. Start drawing and press Cmd+S to save.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
